// src/App.js
import React, { useState, useEffect } from 'react'
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useParams,
  useLocation
} from 'react-router-dom'

import ChatPage         from './components/ChatPage.js'
import MyPrototypesPage from './components/MyPrototypesPage.js'
import useChat          from './hooks/useChat.js'

// ————— SessionEntry: collab session injector —————
function CollabSessionEntry() {
  const { username, chatId } = useParams()
  const { setCollabSessionActive } = useChat()

  useEffect(() => {
    setCollabSessionActive(true)
  }, [setCollabSessionActive])

  return <ChatPage key={`session-${username}-${chatId}`} />
}

// ————— Routes under a “real” username —————
function UserArea() {
  const { username } = useParams()
  const location    = useLocation()

  // Only redirect on home or gallery (prototypes) pages when username mismatches
  const pathParts   = location.pathname.split('/').filter(Boolean)
  const sessionUser = window.__SESSION_USERNAME__
  const isHome       = pathParts.length === 1
  const isPrototypes = pathParts[1] === 'prototypes'

  if (username !== sessionUser && (isHome || isPrototypes)) {
    // peel off suffix after username
    const suffix = pathParts.slice(1).join('/')
    return (
      <Navigate
        to={`/${sessionUser}${suffix ? `/${suffix}` : ''}`}
        replace
      />
    )
  }

  return (
    <Routes>
      {/* GET  /:username                */}
      <Route index element={<ChatPage />} />

      {/* GET  /:username/prototypes     */}
      <Route path="prototypes" element={<MyPrototypesPage />} />

      {/* GET  /:username/:chatId or /:username/:chatId/:version */}
      <Route path=":chatId/:version?" element={<ChatPage />} />

      {/* catch-all under /:username */}
      <Route path="*" element={<div>Page not found</div>} />
    </Routes>
  )
}

export default function App() {
  const [session, setSession] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error,   setError]   = useState(null)

  useEffect(() => {
    fetch('/api/session')
      .then(res => {
        if (!res.ok) throw new Error(`session fetch failed: ${res.status}`)
        return res.json()
      })
      .then(data => {
        if (!data?.username) throw new Error('No username in session payload')
        setSession(data)
        // expose to UserArea redirect logic
        window.__SESSION_USERNAME__ = data.username
      })
      .catch(err => setError(err))
      .finally(() => setLoading(false))
  }, [])

  if (loading) return <div style={{ padding: 20 }}>Loading session…</div>
  if (error)   return <div style={{ padding: 20, color: 'red' }}>
                     Error loading session: {error.message}
                   </div>

  const home = `/${session.username}`

  return (
    <BrowserRouter>
      <Routes>
        {/* root → /my_user_name */}
        <Route path="/" element={<Navigate to={home} replace />} />

        {/* 🎯 Collab session link 👉 must be above the /:username route */}
        <Route path="/s/:username/:chatId" element={<CollabSessionEntry />} />

        {/* everything else under /:username/* */}
        <Route path="/:username/*" element={<UserArea />} />

        {/* fallback → home */}
        <Route path="*" element={<Navigate to={home} replace />} />
      </Routes>
    </BrowserRouter>
  )
}
